/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import React from "react"
import { useSelector } from "react-redux"
import { Button, ConfigProvider, DatePicker, Select } from "@duxton-web/ui"
import { CloseCircleFilled, CloseUniversal } from "@duxton-web/icons"
import { isEmpty } from "lodash"
import dayjs, { Dayjs } from "dayjs"
import {
  ExtraReviewsFilterConfig,
  ReviewStatus,
} from "types/api/menuFeedback.type"
import { AppState } from "store/store.type"
import { useLocaleContext, useTranslationsMap } from "common/utils/i18n"
import useFEConfig from "hooks/useFEConfig"
import { getFeedbackDateRange } from "utils/feedback_helper"
import { getDuxtonLocale, getLocalizedDateFormat } from "utils/i18n"
import { filterDisabledDate } from "./utils"

export interface FeedbackFiltersProps {
  onFetchReviews?(
    extras?: ExtraReviewsFilterConfig & { merchantIDs?: string[] },
  ): any
  merchantID?: string
  isSingleStore?: boolean
  singleStoreID?: string
}

const { RangePicker } = DatePicker

const FeedbackFilters: React.FC<FeedbackFiltersProps> = ({
  onFetchReviews = () => {},
  merchantID,
}) => {
  const {
    feedback: { filterState },
  } = useSelector((state: AppState) => state)
  const feConfig = useFEConfig(merchantID ? { merchantID } : undefined)
  const { startDate, endDate } = getFeedbackDateRange()
  const locale = useLocaleContext("locale")
  const duxtonLocale = getDuxtonLocale(locale)
  const dateDisplayFormat = getLocalizedDateFormat(
    locale,
    "Do/MMM/YYYY",
    "YYYY年M月D日",
  )

  const textMap = useTranslationsMap({
    startDate: "common.startDate",
    endDate: "common.endDate",
    status: "common.status",
    reset: "common.reset",
    statusPublic: "feedback.filters.status.public",
    statusRemoved: "feedback.filters.status.removed",
    statusReported: "feedback.filters.status.reported",
    dineIn: "feedback.filters.serviceType.dineIn",
    delivery: "feedback.filters.serviceType.delivery",
    deliveryAndSelfPickup: "feedback.filters.serviceType.deliveryAndSelfPickup",
    all: "common.all",
    writtenReviews: "feedback.filters.writtenReviews",
  })

  const statusText: { [key: string]: string } = {
    [ReviewStatus.PUBLIC]: textMap.statusPublic,
    [ReviewStatus.REMOVED]: textMap.statusRemoved,
    [ReviewStatus.REPORTED]: textMap.statusReported,
  }

  const onChangeStatusFilter = (values?: string[]) => {
    onFetchReviews({
      status: values as ReviewStatus[],
    })
  }

  const onChangeDateFilter = (value: [Dayjs, Dayjs]) => {
    if (Array.isArray(value) && value.length) {
      onFetchReviews({
        startDate: value[0]?.toISOString(),
        endDate: value[1]?.endOf("d").toISOString(), // include any reviews created on the end date
      })
    } else {
      onFetchReviews({
        startDate,
        endDate,
      })
    }
  }

  const resetFilter = () => {
    onFetchReviews({
      startDate,
      endDate,
      status: [],
      includeEmptyReviews: false,
    })
  }

  const disabledDate = (current: Dayjs) =>
    filterDisabledDate({
      current,
      feConfig,
      filterState,
    })

  const hasDateFilterValue =
    filterState?.startDate &&
    filterState?.endDate &&
    (dayjs(filterState.startDate).diff(dayjs(startDate), "days") !== 0 ||
      dayjs(filterState.endDate).diff(dayjs(endDate), "days") !== 0)

  const defaultDateRange = [dayjs(startDate), dayjs(endDate)] as [Dayjs, Dayjs]

  const includeEmptyReviews = filterState?.includeEmptyReviews ?? false

  return (
    <div
      css={css({
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "16px",
      })}
    >
      <ConfigProvider locale={duxtonLocale}>
        <RangePicker
          data-testid="feedback-dates-selector"
          format={dateDisplayFormat}
          onChange={onChangeDateFilter}
          value={
            hasDateFilterValue
              ? [dayjs(filterState?.startDate), dayjs(filterState?.endDate)]
              : defaultDateRange
          }
          placeholder={[textMap.startDate, textMap.endDate]}
          disabledDate={disabledDate}
          // to prevent showing range selection when clearing the value
          allowClear={{
            clearIcon: <CloseCircleFilled onClick={e => e.stopPropagation()} />,
          }}
          inputReadOnly
        />
      </ConfigProvider>

      <Select
        onChange={onChangeStatusFilter}
        style={{
          width:
            filterState?.status?.length === 0 || !filterState?.status
              ? 150
              : filterState.status.length * 150,
        }}
        allowClear
        placeholder={textMap.status}
        mode="multiple"
        data-testid="feedback-status-selector"
        value={filterState?.status}
      >
        {[ReviewStatus.PUBLIC, ReviewStatus.REMOVED, ReviewStatus.REPORTED].map(
          status => (
            <Select.Option value={status} key={status}>
              {statusText[status]}
            </Select.Option>
          ),
        )}
      </Select>

      <Select
        onClick={() =>
          onFetchReviews({ includeEmptyReviews: !includeEmptyReviews })
        }
        suffixIcon={includeEmptyReviews ? null : <CloseUniversal />}
        placeholder={textMap.writtenReviews || "Written reviews"}
        notFoundContent={null}
        data-testid="feedback-written-reviews-selector"
        css={css({
          ".dui-select-selector":
            includeEmptyReviews === false
              ? {
                  borderColor: "#00804a !important",
                  fontWeight: 600,
                  span: { color: "#00804a !important" },
                }
              : {}, // To be updated once design specs finalized
        })}
      />

      {(hasDateFilterValue || !isEmpty(filterState?.status)) && (
        <Button
          data-testid="feedback-reset-filter"
          onClick={resetFilter}
          type="link"
          css={{
            span: {
              fontWeight: 500,
            },
          }}
        >
          {textMap.reset}
        </Button>
      )}
    </div>
  )
}

export default FeedbackFilters
